import { useEffect, useState, useMemo } from "react"
import {
  Folder, File, FileText, FileVideo, FileImage, FileAudio,
  Upload, Plus, Trash2, Download, Search, Filter,
  ChevronRight, MoreVertical, HardDrive, FolderOpen, X, AlertCircle,
} from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { Badge } from "@/clientWeb/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/clientWeb/components/ui/table"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { filesService, type FileRecord } from "./services/files.service"

type FileItem = FileRecord

const FILE_ICONS: Record<FileItem["type"], React.ReactNode> = {
  folder: <Folder className="w-5 h-5 text-amber-500" />,
  video: <FileVideo className="w-5 h-5 text-purple-500" />,
  document: <FileText className="w-5 h-5 text-blue-500" />,
  image: <FileImage className="w-5 h-5 text-green-500" />,
  audio: <FileAudio className="w-5 h-5 text-pink-500" />,
  other: <File className="w-5 h-5 text-muted-foreground" />,
}

const FILE_BADGE_COLORS: Record<FileItem["type"], string> = {
  folder: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  video: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  document: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  image: "bg-green-500/10 text-green-600 border-green-500/20",
  audio: "bg-pink-500/10 text-pink-600 border-pink-500/20",
  other: "bg-muted text-muted-foreground border-border",
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B"
  const k = 1024
  const sizes = ["B", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i]
}

interface BreadcrumbItem { id: string | null; name: string }

const FOLDER_NAME_MAX = 60

export default function MyFiles() {
  const t = useTranslation()
  const [folderId, setFolderId] = useState<string | null>(null)
  // The root crumb's name is rendered from t() — stored here it stayed in the
  // language the page was opened in.
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([{ id: null, name: "" }])
  const [search, setSearch] = useState("")
  const [typeFilter, setTypeFilter] = useState<string>("all")
  const [selected, setSelected] = useState<string[]>([])
  const [showUpload, setShowUpload] = useState(false)
  const [showNewFolder, setShowNewFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState("")
  const [folderNameError, setFolderNameError] = useState("")
  const [confirmDeleteIds, setConfirmDeleteIds] = useState<string[] | null>(null)
  const [files, setFiles] = useState<FileItem[]>([])
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  const [usage, setUsage] = useState<{ usedBytes: number; totalBytes: number }>({ usedBytes: 0, totalBytes: 0 })

  useEffect(() => {
    let cancelled = false
    Promise.all([filesService.list(), filesService.usage()])
      .then(([list, u]) => {
        if (cancelled) return
        setFiles(list)
        setUsage(u)
      })
      .catch((err) => {
        if (cancelled) return
        toast.error({
          title: t('common.error'),
          description: err instanceof Error && err.message ? err.message : t('myFiles.loadFailed'),
        })
      })
    return () => { cancelled = true }
    // Load once. `t` changes with the language, and listing it here re-fetched
    // every file whenever the language was switched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const usedPct = usage.totalBytes ? Math.round((usage.usedBytes / usage.totalBytes) * 100) : 0

  const visibleFiles = useMemo(() => {
    return files
      .filter(f => f.parentId === folderId)
      .filter(f => typeFilter === "all" || f.type === typeFilter)
      .filter(f => !search || f.name.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => {
        if (a.type === "folder" && b.type !== "folder") return -1
        if (a.type !== "folder" && b.type === "folder") return 1
        return a.name.localeCompare(b.name)
      })
  }, [files, folderId, typeFilter, search])

  const openFolder = (item: FileItem) => {
    if (item.type !== "folder") return
    setFolderId(item.id)
    setBreadcrumbs(prev => [...prev, { id: item.id, name: item.name }])
    setSelected([])
    setSearch("")
  }

  const navigateTo = (crumb: BreadcrumbItem) => {
    setFolderId(crumb.id)
    setBreadcrumbs(prev => {
      const idx = prev.findIndex(c => c.id === crumb.id)
      return prev.slice(0, idx + 1)
    })
    setSelected([])
  }

  const toggleSelect = (id: string) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const handleDelete = (ids: string[]) => {
    setFiles(prev => prev.filter(f => !ids.includes(f.id)))
    setSelected([])
    toast.error({ title: t("myFiles.fileDeleted"), description: t("common.itemsRemoved") })
  }

  const handleCreateFolder = () => {
    const trimmed = newFolderName.trim()
    if (trimmed.length < 1) {
      setFolderNameError('myFiles.folderNameRequired')
      return
    }
    if (trimmed.length > FOLDER_NAME_MAX) {
      setFolderNameError('myFiles.folderNameTooLong')
      return
    }
    if (!/^[\w\s\-.()&]+$/.test(trimmed)) {
      setFolderNameError('myFiles.folderNameInvalid')
      return
    }
    const exists = files.some(f => f.type === 'folder' && f.parentId === folderId && f.name.toLowerCase() === trimmed.toLowerCase())
    if (exists) {
      setFolderNameError('myFiles.folderNameExists')
      return
    }
    const newFolder: FileItem = {
      id: `folder-${Date.now()}`,
      name: trimmed,
      type: "folder",
      size: "—",
      sizeBytes: 0,
      uploadedAt: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      parentId: folderId,
    }
    setFiles(prev => [...prev, newFolder])
    setNewFolderName("")
    setFolderNameError("")
    setShowNewFolder(false)
    toast.success({ title: t("common.folderCreated"), description: t("common.folderCreatedDesc") })
  }

  const handleDownload = (_name: string) => {
    toast.success({ title: t("common.downloading"), description: t("common.downloadWillBegin") })
  }

  return (
    <div className="flex flex-col h-full gap-5 p-1">
      {/* Storage Bar */}
      <div className="flex flex-col gap-1">
        <h2 className="text-xl text-foreground font-bold tracking-tight">
          {t("myFiles.title")}
        </h2>
        <p className="text-muted-foreground text-sm">
          {t("myFiles.storageUsed")}
        </p>
      </div>
      <div className="bg-card rounded-2xl border border-stroke-100 dark:border-border p-5 shadow-xs">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <HardDrive className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">{t("myFiles.storageUsed")}</h3>
            <p className="text-xs text-muted-foreground">{formatBytes(usage.usedBytes)} used of {formatBytes(usage.totalBytes)}</p>
          </div>
          <div className="ml-auto text-right">
            <span className="text-lg font-bold text-foreground">{usedPct}%</span>
            <p className="text-[10px] text-muted-foreground">{formatBytes(Math.max(0, usage.totalBytes - usage.usedBytes))} free</p>
          </div>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary to-primary/70 transition-all"
            style={{ width: `${usedPct}%` }}
          />
        </div>
        <div className="flex items-center gap-4 mt-3 flex-wrap">
          {[
            { label: t("myFiles.legend.videos"), color: "bg-purple-500", bytes: files.filter(f => f.type === "video").reduce((s, f) => s + f.sizeBytes, 0) },
            { label: t("myFiles.legend.documents"), color: "bg-blue-500", bytes: files.filter(f => f.type === "document").reduce((s, f) => s + f.sizeBytes, 0) },
            { label: t("myFiles.legend.images"), color: "bg-green-500", bytes: files.filter(f => f.type === "image").reduce((s, f) => s + f.sizeBytes, 0) },
            { label: t("myFiles.legend.audio"), color: "bg-pink-500", bytes: files.filter(f => f.type === "audio").reduce((s, f) => s + f.sizeBytes, 0) },
          ].map(({ label, color, bytes }) => (
            <div key={label} className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${color}`} />
              <span className="text-xs text-muted-foreground">{label}</span>
              <span className="text-xs font-medium text-foreground">{formatBytes(bytes)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* File Browser */}
      <div className="bg-card rounded-2xl border border-border shadow-sm flex flex-col flex-1 overflow-hidden min-h-0">
        {/* Toolbar */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border flex-wrap">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-1 flex-1 min-w-0">
            {breadcrumbs.map((crumb, i) => (
              <div key={i} className="flex items-center gap-1">
                {i > 0 && <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />}
                <button
                  onClick={() => navigateTo(crumb)}
                  className={`text-xs font-medium rounded px-1 py-0.5 transition-colors ${i === breadcrumbs.length - 1
                      ? "text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                    }`}
                >
                  {i === 0 ? <div className="flex items-center gap-1"><FolderOpen className="w-3.5 h-3.5" />{t("myFiles.title")}</div> : crumb.name}
                </button>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            {selected.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                className="h-8 text-xs gap-1.5"
                onClick={() => setConfirmDeleteIds(selected)}
              >
                <Trash2 className="w-3.5 h-3.5" />
                {t("common.delete")} ({selected.length})
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={() => setShowNewFolder(true)}>
              <Plus className="w-3.5 h-3.5" />
              {t("common.newFolder")}
            </Button>
            <Button size="sm" className="h-8 text-xs gap-1.5" onClick={() => setShowUpload(true)}>
              <Upload className="w-3.5 h-3.5" />
              {t("myFiles.uploadFile")}
            </Button>
          </div>
        </div>

        {/* Search + Filter */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border">
          <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-1.5 flex-1 min-w-0">
            <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t("myFiles.searchFiles")}
              className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground"
            />
            {search && (
              <button onClick={() => setSearch("")}><X className="w-3 h-3 text-muted-foreground" /></button>
            )}
          </div>
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            {(["all", "folder", "video", "document", "image", "audio"] as const).map(type => (
              <button
                key={type}
                onClick={() => setTypeFilter(type)}
                aria-label={type === "all" ? t("common.all") : undefined}
                aria-pressed={typeFilter === type}
                className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-all ${typeFilter === type ? "bg-card shadow-sm text-primary" : "text-muted-foreground"
                  }`}
              >
                {type === "all" ? <Filter className="w-3.5 h-3.5" /> : t(`myFiles.types.${type}`)}
              </button>
            ))}
          </div>
        </div>

        {/* File Table */}
        <div className="flex-1 overflow-y-auto">
          {visibleFiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-16 gap-3 text-center">
              <FolderOpen className="w-12 h-12 text-muted-foreground/30" />
              <p className="text-sm font-medium text-muted-foreground">{t("myFiles.noFiles")}</p>
              <p className="text-xs text-muted-foreground/60">{t("myFiles.uploadYourFirst")}</p>
            </div>
          ) : (
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow className="border-b border-border">
                  <TableHead className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground w-8">
                    <input
                      type="checkbox"
                      className="rounded accent-primary"
                      checked={selected.length === visibleFiles.length && visibleFiles.length > 0}
                      onChange={e => setSelected(e.target.checked ? visibleFiles.map(f => f.id) : [])}
                    />
                  </TableHead>
                  <TableHead className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">{t("myFiles.fileName")}</TableHead>
                  <TableHead className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">{t("common.type")}</TableHead>
                  <TableHead className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">{t("myFiles.size")}</TableHead>
                  <TableHead className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">{t("myFiles.uploadedOn")}</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleFiles.map(file => (
                  <TableRow
                    key={file.id}
                    className={`border-b border-border/50 hover:bg-muted/20 transition-colors ${selected.includes(file.id) ? "bg-primary/5" : ""}`}
                    onDoubleClick={() => file.type === "folder" && openFolder(file)}
                  >
                    <TableCell className="px-4 py-3">
                      <input
                        type="checkbox"
                        className="rounded accent-primary"
                        checked={selected.includes(file.id)}
                        onChange={() => toggleSelect(file.id)}
                      />
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <button
                        className="flex items-center gap-2.5 text-left group"
                        onClick={() => file.type === "folder" ? openFolder(file) : undefined}
                      >
                        <span className="shrink-0">{FILE_ICONS[file.type]}</span>
                        <span className={`text-sm font-medium text-foreground truncate max-w-[200px] ${file.type === "folder" ? "group-hover:text-primary transition-colors" : ""}`}>
                          {file.name}
                        </span>
                      </button>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <Badge className={`text-[10px] font-semibold capitalize ${FILE_BADGE_COLORS[file.type]}`}>
                        {t(`myFiles.types.${file.type}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-xs text-muted-foreground">{file.size}</TableCell>
                    <TableCell className="px-4 py-3 text-xs text-muted-foreground">{file.uploadedAt}</TableCell>
                    <TableCell className="px-4 py-3">
                      <div className="relative">
                        <button
                          onClick={() => setMenuOpenId(menuOpenId === file.id ? null : file.id)}
                          className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-muted transition-colors"
                        >
                          <MoreVertical className="w-4 h-4 text-muted-foreground" />
                        </button>
                        {menuOpenId === file.id && (
                          <div className="absolute right-0 top-8 z-50 bg-card border border-border rounded-xl shadow-lg py-1 w-36 text-sm">
                            {file.type !== "folder" && (
                              <button
                                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted transition-colors text-foreground"
                                onClick={() => { handleDownload(file.name); setMenuOpenId(null) }}
                              >
                                <Download className="w-3.5 h-3.5" />
                                {t("myFiles.download")}
                              </button>
                            )}
                            <button
                              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 transition-colors"
                              onClick={() => { setConfirmDeleteIds([file.id]); setMenuOpenId(null) }}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              {t("common.delete")}
                            </button>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Status bar */}
        <div className="px-4 py-2 border-t border-border flex items-center gap-2 shrink-0">
          <span className="text-xs text-muted-foreground">
            {visibleFiles.length === 1 ? t("myFiles.itemCountOne") : t("myFiles.itemCount", { count: visibleFiles.length })}
          </span>
          {selected.length > 0 && (
            <span className="text-xs text-primary font-medium">· {t("myFiles.selectedCount", { count: selected.length })}</span>
          )}
        </div>
      </div>

      {/* New Folder Dialog */}
      {showNewFolder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowNewFolder(false)} />
          <div className="relative bg-card rounded-2xl border border-border shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-base font-semibold text-foreground mb-4">{t("common.newFolder")}</h3>
            <input
              autoFocus
              value={newFolderName}
              onChange={e => { setNewFolderName(e.target.value.slice(0, FOLDER_NAME_MAX)); if (folderNameError) setFolderNameError("") }}
              onKeyDown={e => e.key === "Enter" && handleCreateFolder()}
              placeholder={t("common.folderName")}
              maxLength={FOLDER_NAME_MAX}
              className={`w-full border rounded-xl px-3 py-2.5 text-sm bg-background text-foreground outline-none focus:ring-2 mb-1 ${folderNameError ? 'border-destructive focus:ring-destructive/30' : 'border-border focus:ring-primary/30'}`}
            />
            <div className="flex justify-between mb-4">
              {folderNameError ? <p className="text-xs text-destructive">{t(folderNameError, { max: FOLDER_NAME_MAX })}</p> : <span />}
              <p className="text-xs text-muted-foreground">{newFolderName.length}/{FOLDER_NAME_MAX}</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => { setShowNewFolder(false); setFolderNameError(""); setNewFolderName("") }}>{t("common.cancel")}</Button>
              <Button className="flex-1" onClick={handleCreateFolder} disabled={!newFolderName.trim()}>{t("common.create")}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {confirmDeleteIds && confirmDeleteIds.length > 0 && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setConfirmDeleteIds(null)} />
          <div className="relative bg-card rounded-2xl shadow-2xl border border-border p-6 w-full max-w-sm">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-foreground text-center mb-1">
              {confirmDeleteIds.length === 1 ? t("myFiles.deleteItemTitle") : t("myFiles.deleteItemsTitle", { count: confirmDeleteIds.length })}
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-6">
              {t("myFiles.deleteItemsDesc")}
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setConfirmDeleteIds(null)}>
                {t("common.cancel")}
              </Button>
              <Button
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                onClick={() => { handleDelete(confirmDeleteIds); setConfirmDeleteIds(null) }}
              >
                {t("common.delete")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Dialog */}
      {showUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowUpload(false)} />
          <div className="relative bg-card rounded-2xl border border-border shadow-2xl p-6 w-full max-w-md">
            <h3 className="text-base font-semibold text-foreground mb-1">{t("myFiles.uploadFile")}</h3>
            <p className="text-xs text-muted-foreground mb-5">{t("common.maxFileSize")}</p>
            <div className="border-2 border-dashed border-border rounded-2xl p-10 flex flex-col items-center gap-3 hover:border-primary/50 transition-colors cursor-pointer bg-muted/20">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Upload className="w-6 h-6 text-primary" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-foreground">{t("common.dropFilesHere")}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("common.orClickToBrowse")}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
              <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-300">{t("common.supportedFormats")}</p>
            </div>
            <div className="flex gap-3 mt-4">
              <Button variant="outline" className="flex-1" onClick={() => setShowUpload(false)}>{t("common.cancel")}</Button>
              <Button className="flex-1" onClick={() => { setShowUpload(false); toast.success({ title: t("common.filesUploaded"), description: t("common.filesUploadedDesc") }) }}>
                <Upload className="w-4 h-4 mr-1.5" />
                {t("myFiles.uploadFile")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
